import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Users, 
  Trophy, 
  MessageCircle, 
  Plus, 
  Search, 
  Filter,
  Heart,
  ThumbsUp,
  Share2,
  Calendar,
  Target,
  Award,
  Clock,
  MapPin,
  Star
} from 'lucide-react';

interface Group {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  category: string;
  isJoined: boolean;
  recentActivity: string;
  lastActive: Date;
}

interface Challenge {
  id: string;
  name: string;
  description: string;
  type: 'fitness' | 'nutrition' | 'lifestyle' | 'social';
  duration: number;
  participants: number;
  isJoined: boolean;
  progress?: number;
  endDate: Date;
  rewards: string[];
}

interface Post {
  id: string;
  author: {
    name: string;
    avatar: string;
    age: number;
  };
  content: string;
  type: 'workout' | 'nutrition' | 'achievement' | 'question' | 'motivation';
  likes: number;
  comments: number;
  timestamp: Date;
  groupId?: string;
}

interface CommunityPanelProps {
  userId: string;
  userProfile: any;
  onJoinGroup?: (groupId: string) => void;
  onJoinChallenge?: (challengeId: string) => void;
  onLikePost?: (postId: string) => void;
  onCommentPost?: (postId: string, comment: string) => void;
}

export const CommunityPanel: React.FC<CommunityPanelProps> = ({
  userId,
  userProfile,
  onJoinGroup,
  onJoinChallenge,
  onLikePost,
  onCommentPost
}) => {
  const [activeTab, setActiveTab] = useState<'groups' | 'challenges' | 'feed'>('groups');
  const [groups, setGroups] = useState<Group[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [feedFilter, setFeedFilter] = useState<'all' | 'myGroups'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  useEffect(() => {
    // Load community data
    loadCommunityData();
  }, []);

  const loadCommunityData = async () => {
    // Mock data - in production, this would come from the CommunityBee
    setGroups([
      {
        id: '1',
        name: 'Fit at 40 Club',
        description: 'Supporting each other through fitness journeys after 40',
        memberCount: 1247,
        category: 'age_specific',
        isJoined: true,
        recentActivity: 'Toshika completed her 5K run!',
        lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000)
      },
      {
        id: '2',
        name: 'Working Moms Fitness',
        description: 'Balancing fitness with motherhood and career',
        memberCount: 892,
        category: 'lifestyle',
        isJoined: false,
        recentActivity: 'New recipe shared: High-protein breakfast bowl',
        lastActive: new Date(Date.now() - 4 * 60 * 60 * 1000)
      },
      {
        id: '3',
        name: 'Weightlifting Queens',
        description: 'Strength training and muscle building for women',
        memberCount: 2156,
        category: 'fitness',
        isJoined: true,
        recentActivity: 'Monthly PR challenge starting soon!',
        lastActive: new Date(Date.now() - 1 * 60 * 60 * 1000)
      }
    ]);

    setChallenges([
      {
        id: '1',
        name: '30-Day Step Challenge',
        description: 'Walk 10,000 steps daily for 30 days',
        type: 'fitness',
        duration: 30,
        participants: 456,
        isJoined: true,
        progress: 65,
        endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
        rewards: ['Completion badge', 'Fitness tracker discount']
      },
      {
        id: '2',
        name: 'Protein Power Week',
        description: 'Hit your protein goals for 7 days straight',
        type: 'nutrition',
        duration: 7,
        participants: 234,
        isJoined: false,
        endDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
        rewards: ['Nutrition badge', 'Recipe collection']
      }
    ]);

    setPosts([
      {
        id: '1',
        author: {
          name: 'Jennifer M.',
          avatar: '/avatars/jennifer.jpg',
          age: 42
        },
        content: 'Just completed my first deadlift at 135 lbs! Never thought I could do this at 42. The support from this group has been incredible! 💪',
        type: 'achievement',
        likes: 23,
        comments: 8,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        groupId: '3'
      },
      {
        id: '2',
        author: {
          name: 'Maria S.',
          avatar: '/avatars/maria.jpg',
          age: 38
        },
        content: 'Looking for quick breakfast ideas that are high in protein and can be prepped the night before. Any suggestions?',
        type: 'question',
        likes: 5,
        comments: 12,
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        groupId: '2'
      }
    ]);
  };

  const filteredGroups = groups.filter(group => 
    (filterCategory === 'all' || group.category === filterCategory) &&
    group.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredChallenges = challenges.filter(challenge =>
    challenge.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'age_specific': return <Users className="w-4 h-4" />;
      case 'lifestyle': return <Heart className="w-4 h-4" />;
      case 'fitness': return <Target className="w-4 h-4" />;
      case 'nutrition': return <Award className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getChallengeIcon = (type: string) => {
    switch (type) {
      case 'fitness': return <Target className="w-5 h-5" />;
      case 'nutrition': return <Award className="w-5 h-5" />;
      case 'lifestyle': return <Heart className="w-5 h-5" />;
      case 'social': return <Users className="w-5 h-5" />;
      default: return <Trophy className="w-5 h-5" />;
    }
  };

  const getPostIcon = (type: string) => {
    switch (type) {
      case 'workout': return <Target className="w-4 h-4" />;
      case 'nutrition': return <Award className="w-4 h-4" />;
      case 'achievement': return <Trophy className="w-4 h-4" />;
      case 'question': return <MessageCircle className="w-4 h-4" />;
      case 'motivation': return <Heart className="w-4 h-4" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="flex flex-col h-full sculptd-card-elevated sculptd-sheen" style={{
      boxShadow: 'var(--shadow-card-separation)',
      border: '1px solid rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-6 border-b" style={{ 
        borderColor: 'rgba(58, 175, 169, 0.2)',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(58, 175, 169, 0.05) 100%)'
      }}>
        <div>
          <h1 className="text-2xl font-bold mb-1" style={{ 
            color: 'var(--color-text)',
            background: 'linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>Community</h1>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            Connect, support, and grow together
          </p>
        </div>
        <button className="p-3 text-white rounded-xl transition-all duration-200 hover:scale-105 hover:shadow-lg" style={{ 
          background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)', 
          boxShadow: '0 4px 12px rgba(58, 175, 169, 0.4)',
          textShadow: '0 1px 2px rgba(0,0,0,0.2)'
        }}>
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex p-2" style={{ 
        background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(58, 175, 169, 0.05) 100%)',
        borderBottom: '1px solid rgba(58, 175, 169, 0.2)'
      }}>
        <button
          onClick={() => setActiveTab('groups')}
          className={`flex-1 py-3 px-6 text-sm font-bold rounded-xl transition-all duration-200 ${
            activeTab === 'groups'
              ? 'shadow-lg'
              : 'hover:shadow-md'
          }`}
          style={{ 
            background: activeTab === 'groups' 
              ? 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)' 
              : 'transparent',
            color: activeTab === 'groups' ? 'white' : 'var(--color-text-muted)',
            boxShadow: activeTab === 'groups' ? '0 4px 12px rgba(58, 175, 169, 0.4)' : 'none',
            textShadow: activeTab === 'groups' ? '0 1px 2px rgba(0,0,0,0.2)' : 'none'
          }}
        >
          👥 Groups
        </button>
        <button
          onClick={() => setActiveTab('challenges')}
          className={`flex-1 py-3 px-6 text-sm font-bold rounded-xl transition-all duration-200 mx-1 ${
            activeTab === 'challenges'
              ? 'shadow-lg'
              : 'hover:shadow-md'
          }`}
          style={{ 
            background: activeTab === 'challenges' 
              ? 'linear-gradient(135deg, var(--color-accent) 0%, var(--color-blush) 100%)' 
              : 'transparent',
            color: activeTab === 'challenges' ? 'white' : 'var(--color-text-muted)',
            boxShadow: activeTab === 'challenges' ? '0 4px 12px rgba(156, 106, 222, 0.4)' : 'none',
            textShadow: activeTab === 'challenges' ? '0 1px 2px rgba(0,0,0,0.2)' : 'none'
          }}
        >
          🏆 Challenges
        </button>
        <button
          onClick={() => setActiveTab('feed')}
          className={`flex-1 py-3 px-6 text-sm font-bold rounded-xl transition-all duration-200 ${
            activeTab === 'feed'
              ? 'shadow-lg'
              : 'hover:shadow-md'
          }`}
          style={{ 
            background: activeTab === 'feed' 
              ? 'linear-gradient(135deg, var(--color-blush) 0%, var(--color-primary) 100%)' 
              : 'transparent',
            color: activeTab === 'feed' ? 'white' : 'var(--color-text-muted)',
            boxShadow: activeTab === 'feed' ? '0 4px 12px rgba(244, 182, 194, 0.4)' : 'none',
            textShadow: activeTab === 'feed' ? '0 1px 2px rgba(0,0,0,0.2)' : 'none'
          }}
        >
          💬 Feed
        </button>
      </div>

      {/* Search and Filter */}
      <div className="px-6 py-6" style={{ 
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
        borderBottom: '1px solid rgba(58, 175, 169, 0.1)'
      }}>
        <div className="flex items-center space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--color-primary)' }} />
            <input
              type="text"
              placeholder="Search groups, challenges, or posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border-2 rounded-xl focus:outline-none focus:ring-0 focus:border-transparent transition-all duration-200"
              style={{ 
                borderColor: 'rgba(58, 175, 169, 0.2)', 
                background: 'rgba(255,255,255,0.8)',
                color: 'var(--color-text)',
                fontSize: '16px',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--color-primary)';
                e.target.style.boxShadow = '0 0 0 3px rgba(58, 175, 169, 0.1), 0 4px 12px rgba(58, 175, 169, 0.2)';
                e.target.style.background = 'white';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(58, 175, 169, 0.2)';
                e.target.style.boxShadow = 'none';
                e.target.style.background = 'rgba(255,255,255,0.8)';
              }}
            />
          </div>
          <button className="p-4 border-2 rounded-xl hover:shadow-lg transition-all duration-200" style={{ 
            borderColor: 'rgba(58, 175, 169, 0.2)', 
            background: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)'
          }}>
            <Filter className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
          </button>
        </div>
        
        {activeTab === 'groups' && (
          <div className="flex items-center space-x-3 mt-2">
            <span className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Category:</span>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="text-sm border rounded px-2 py-1"
              style={{ 
                borderColor: 'var(--color-border)', 
                background: 'white',
                color: 'var(--color-text)',
                textShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <option value="all">All</option>
              <option value="age_specific">Age Specific</option>
              <option value="lifestyle">Lifestyle</option>
              <option value="fitness">Fitness</option>
              <option value="nutrition">Nutrition</option>
            </select>
          </div>
        )}
        
        {/* Group Context and Feed Filter - Only show in Feed tab */}
        {activeTab === 'feed' && (
          <div className="mt-4 space-y-3">
            {/* Current Group Context */}
            <div className="flex items-center justify-between p-4 rounded-lg" style={{
              background: 'var(--gradient-card-bg)',
              border: '1px solid var(--color-border)'
            }}>
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
                <div>
                  <h4 className="font-semibold" style={{ color: 'var(--color-text)' }}>
                    Women 35+ Wellness Circle
                  </h4>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    127 members • 23 active today
                  </p>
                </div>
              </div>
              <button
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                style={{
                  background: 'var(--gradient-primary)',
                  color: 'white',
                  boxShadow: 'var(--shadow-sm)'
                }}
              >
                Leave Group
              </button>
            </div>
            
            {/* Feed Filter Toggle */}
            <div className="flex space-x-2">
              <button
                onClick={() => setFeedFilter('all')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                  feedFilter === 'all' ? 'shadow-md' : ''
                }`}
                style={{
                  background: feedFilter === 'all' 
                    ? 'var(--gradient-primary)' 
                    : 'var(--gradient-card-bg)',
                  color: feedFilter === 'all' 
                    ? 'white' 
                    : 'var(--color-text)',
                  border: feedFilter === 'all' 
                    ? 'none' 
                    : '1px solid var(--color-border)'
                }}
              >
                All Community Posts
              </button>
              <button
                onClick={() => setFeedFilter('myGroups')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                  feedFilter === 'myGroups' ? 'shadow-md' : ''
                }`}
                style={{
                  background: feedFilter === 'myGroups' 
                    ? 'var(--gradient-primary)' 
                    : 'var(--gradient-card-bg)',
                  color: feedFilter === 'myGroups' 
                    ? 'white' 
                    : 'var(--color-text)',
                  border: feedFilter === 'myGroups' 
                    ? 'none' 
                    : '1px solid var(--color-border)'
                }}
              >
                My Groups
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {activeTab === 'groups' && (
          <div className="space-y-4">
            {filteredGroups.map((group) => (
              <Card key={group.id} className="sculptd-card-elevated sculptd-sheen hover:shadow-lg transition-all duration-300 relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                border: '1px solid rgba(255,255,255,0.6)',
                backdropFilter: 'blur(20px)'
              }}>
                <CardContent className="p-4 relative z-10">
                  {/* Header Section */}
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ 
                      background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
                      boxShadow: '0 4px 12px rgba(58, 175, 169, 0.3)'
                    }}>
                      {getCategoryIcon(group.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold truncate" style={{ 
                        color: 'var(--color-text)', 
                        textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>{group.name}</h3>
                      {group.isJoined && (
                        <span className="inline-block px-2 py-1 text-xs font-bold rounded-full mt-1" style={{ 
                          background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)', 
                          color: 'white'
                        }}>
                          ✓ JOINED
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm mb-3 leading-relaxed" style={{ 
                    color: 'var(--color-text)', 
                    lineHeight: '1.5'
                  }}>{group.description}</p>

                  {/* Stats Section - Single Row */}
                  <div className="flex items-center justify-between mb-3 space-x-4">
                    <div className="flex items-center space-x-2 flex-1">
                      <Users className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
                      <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                        {group.memberCount.toLocaleString()}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>members</span>
                    </div>
                    <div className="flex items-center space-x-2 flex-1">
                      <Clock className="w-4 h-4" style={{ color: 'var(--color-accent)' }} />
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        Active {group.lastActive.toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="p-3 rounded-lg mb-4" style={{ 
                    background: 'linear-gradient(135deg, rgba(244, 182, 194, 0.1) 0%, rgba(156, 106, 222, 0.1) 100%)',
                    border: '1px solid rgba(244, 182, 194, 0.2)'
                  }}>
                    <div className="text-xs font-semibold mb-1" style={{ color: 'var(--color-blush)' }}>
                      Recent Activity
                    </div>
                    <p className="text-sm" style={{ color: 'var(--color-text)' }}>{group.recentActivity}</p>
                  </div>

                  {/* Centered Join/Leave Button */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => onJoinGroup?.(group.id)}
                      className="px-8 py-3 rounded-lg text-sm font-bold transition-all duration-200 hover:scale-105 min-w-[120px]"
                      style={{
                        background: group.isJoined 
                          ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                          : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: 'white',
                        boxShadow: group.isJoined 
                          ? '0 4px 12px rgba(239, 68, 68, 0.3)' 
                          : '0 4px 12px rgba(34, 197, 94, 0.3)'
                      }}
                    >
                      {group.isJoined ? 'Leave' : 'Join'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {activeTab === 'challenges' && (
          <div className="space-y-4">
            {filteredChallenges.map((challenge) => (
              <Card key={challenge.id} className="sculptd-card-elevated sculptd-sheen hover:shadow-lg transition-all duration-300 relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                border: '1px solid rgba(255,255,255,0.6)',
                backdropFilter: 'blur(20px)'
              }}>
                <CardContent className="p-4 relative z-10">
                  {/* Header Section */}
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ 
                      background: 'linear-gradient(135deg, var(--color-accent) 0%, var(--color-blush) 100%)',
                      boxShadow: '0 4px 12px rgba(156, 106, 222, 0.3)'
                    }}>
                      {getChallengeIcon(challenge.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold truncate" style={{ 
                        color: 'var(--color-text)', 
                        textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>{challenge.name}</h3>
                      {challenge.isJoined && (
                        <span className="inline-block px-2 py-1 text-xs font-bold rounded-full mt-1" style={{ 
                          background: 'linear-gradient(135deg, var(--color-accent) 0%, var(--color-blush) 100%)', 
                          color: 'white'
                        }}>
                          🏆 ACTIVE
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm mb-3 leading-relaxed" style={{ 
                    color: 'var(--color-text)', 
                    lineHeight: '1.5'
                  }}>{challenge.description}</p>

                  {/* Stats Section - Single Row */}
                  <div className="flex items-center justify-between mb-3 space-x-2">
                    <div className="flex items-center space-x-1 flex-1">
                      <Users className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
                      <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                        {challenge.participants}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>people</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-1">
                      <Calendar className="w-4 h-4" style={{ color: 'var(--color-accent)' }} />
                      <span className="text-sm font-semibold" style={{ color: 'var(--color-accent)' }}>
                        {challenge.duration}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>days</span>
                    </div>
                    <div className="flex items-center space-x-1 flex-1">
                      <Clock className="w-4 h-4" style={{ color: 'var(--color-blush)' }} />
                      <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        Ends {challenge.endDate.toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Progress Section */}
                  {challenge.progress !== undefined && (
                    <div className="mb-3 p-3 rounded-lg" style={{ 
                      background: 'linear-gradient(135deg, rgba(58, 175, 169, 0.1) 0%, rgba(156, 106, 222, 0.1) 100%)',
                      border: '1px solid rgba(58, 175, 169, 0.2)'
                    }}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>Your Progress</span>
                        <span className="text-lg font-bold" style={{ color: 'var(--color-accent)' }}>{challenge.progress}%</span>
                      </div>
                      <div className="w-full rounded-full h-2" style={{ background: 'rgba(255,255,255,0.5)' }}>
                        <div
                          className="h-2 rounded-full transition-all duration-500"
                          style={{ 
                            background: 'linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)', 
                            width: `${challenge.progress}%`,
                            boxShadow: '0 2px 8px rgba(58, 175, 169, 0.3)'
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Rewards Section */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {challenge.rewards.map((reward, index) => (
                      <span key={index} className="px-2 py-1 text-xs font-semibold rounded-full" style={{ 
                        background: 'linear-gradient(135deg, var(--color-accent) 0%, var(--color-blush) 100%)', 
                        color: 'white',
                        boxShadow: '0 2px 6px rgba(156, 106, 222, 0.3)'
                      }}>
                        🎁 {reward}
                      </span>
                    ))}
                  </div>

                  {/* Centered Join/Leave Button */}
                  <div className="flex justify-center">
                    <button
                      onClick={() => onJoinChallenge?.(challenge.id)}
                      className="px-8 py-3 rounded-lg text-sm font-bold transition-all duration-200 hover:scale-105 min-w-[120px]"
                      style={{
                        background: challenge.isJoined 
                          ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                          : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: 'white',
                        boxShadow: challenge.isJoined 
                          ? '0 4px 12px rgba(239, 68, 68, 0.3)' 
                          : '0 4px 12px rgba(34, 197, 94, 0.3)'
                      }}
                    >
                      {challenge.isJoined ? 'Leave' : 'Join'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {activeTab === 'feed' && (
          <div className="space-y-6">
            {/* Pinned Posts Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2" style={{ color: 'var(--color-text)' }}>
                <Star className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                <span>Pinned Posts</span>
              </h3>
              
              {/* This Week's Challenge - Pinned Post */}
              <Card className="sculptd-card-elevated sculptd-sheen relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, rgba(156, 106, 222, 0.1) 0%, rgba(58, 175, 169, 0.1) 100%)',
                boxShadow: 'var(--shadow-card-separation)',
                border: '2px solid var(--color-accent)',
                backdropFilter: 'blur(20px)'
              }}>
                <CardContent className="p-6 relative z-10">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center relative overflow-hidden" style={{ 
                      background: 'var(--gradient-accent)',
                      boxShadow: 'var(--shadow-md)'
                    }}>
                      <Trophy className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <h4 className="font-bold text-lg" style={{ color: 'var(--color-text)' }}>This Week's Challenge</h4>
                        <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ 
                          background: 'var(--gradient-accent)', 
                          color: 'white' 
                        }}>PINNED</span>
                      </div>
                      <p className="text-base mb-4 leading-relaxed" style={{ 
                        color: 'var(--color-text)', 
                        textShadow: '0 1px 2px rgba(0,0,0,0.05)' 
                      }}>
                        💪 Week 3: "Mindful Movement Monday" - Share a photo of your favorite gentle exercise this week! 
                        Whether it's yoga, walking, or stretching, let's celebrate movement that feels good for our bodies.
                      </p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="flex items-center space-x-1" style={{ color: 'var(--color-text-muted)' }}>
                          <Users className="w-4 h-4" />
                          <span>47 participants</span>
                        </span>
                        <span className="flex items-center space-x-1" style={{ color: 'var(--color-text-muted)' }}>
                          <Calendar className="w-4 h-4" />
                          <span>Ends Friday</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Group Progress - Pinned Post */}
              <Card className="sculptd-card-elevated sculptd-sheen relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, rgba(58, 175, 169, 0.1) 0%, rgba(244, 182, 194, 0.1) 100%)',
                boxShadow: 'var(--shadow-card-separation)',
                border: '2px solid var(--color-primary)',
                backdropFilter: 'blur(20px)'
              }}>
                <CardContent className="p-6 relative z-10">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center relative overflow-hidden" style={{ 
                      background: 'var(--gradient-primary)',
                      boxShadow: 'var(--shadow-md)'
                    }}>
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <h4 className="font-bold text-lg" style={{ color: 'var(--color-text)' }}>Group Progress This Week</h4>
                        <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ 
                          background: 'var(--gradient-primary)', 
                          color: 'white' 
                        }}>PINNED</span>
                      </div>
                      <p className="text-base mb-4 leading-relaxed" style={{ 
                        color: 'var(--color-text)', 
                        textShadow: '0 1px 2px rgba(0,0,0,0.05)' 
                      }}>
                        🎉 Amazing progress, everyone! Our group has completed 89 workouts this week and shared 34 healthy meals. 
                        Keep supporting each other - you're all doing incredible!
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-3 rounded-lg" style={{ background: 'rgba(58, 175, 169, 0.1)' }}>
                          <div className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>89</div>
                          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Workouts</div>
                        </div>
                        <div className="p-3 rounded-lg" style={{ background: 'rgba(156, 106, 222, 0.1)' }}>
                          <div className="text-2xl font-bold" style={{ color: 'var(--color-accent)' }}>34</div>
                          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Healthy Meals</div>
                        </div>
                        <div className="p-3 rounded-lg" style={{ background: 'rgba(244, 182, 194, 0.1)' }}>
                          <div className="text-2xl font-bold" style={{ color: 'var(--color-blush)' }}>127</div>
                          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Kudos Given</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Regular Posts */}
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
                Community Posts
              </h3>
              <div className="space-y-6">
                {posts.map((post) => (
              <Card key={post.id} className="sculptd-card-elevated sculptd-sheen hover:shadow-xl transition-all duration-300 relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                boxShadow: 'var(--shadow-card-separation)',
                border: '1px solid rgba(255,255,255,0.4)',
                backdropFilter: 'blur(20px)'
              }}>
                <CardContent className="p-6 relative z-10">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center relative overflow-hidden" style={{ 
                      background: 'var(--gradient-wellness)',
                      boxShadow: 'var(--shadow-md)'
                    }}>
                      <span className="text-white font-bold text-lg">
                        {post.author.name.charAt(0)}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <h4 className="font-bold text-lg" style={{ color: 'var(--color-text)' }}>{post.author.name}</h4>
                        <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                          background: 'var(--gradient-spa)', 
                          color: 'white' 
                        }}>Age {post.author.age}</span>
                        <div className="flex items-center space-x-1 px-2 py-1 rounded-full" style={{ 
                          background: 'rgba(58, 175, 169, 0.1)',
                          color: 'var(--color-primary)'
                        }}>
                          {getPostIcon(post.type)}
                          <span className="text-xs font-medium capitalize">{post.type}</span>
                        </div>
                      </div>
                      <p className="text-base mb-4 leading-relaxed" style={{ 
                        color: 'var(--color-text)', 
                        textShadow: '0 1px 2px rgba(0,0,0,0.05)' 
                      }}>{post.content}</p>
                      <div className="flex items-center space-x-6 text-sm">
                        <button
                          onClick={() => onLikePost?.(post.id)}
                          className="flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-200 hover:scale-105"
                          style={{
                            background: 'var(--gradient-accent-button)',
                            color: 'white',
                            boxShadow: 'var(--shadow-sm)'
                          }}
                        >
                          <Heart className="w-4 h-4" />
                          <span className="font-medium">{post.likes} Kudos</span>
                        </button>
                        <button className="flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-200 hover:scale-105" style={{
                          background: 'rgba(58, 175, 169, 0.1)',
                          color: 'var(--color-primary)'
                        }}>
                          <MessageCircle className="w-4 h-4" />
                          <span className="font-medium">{post.comments}</span>
                        </button>
                        <button className="flex items-center space-x-2 px-3 py-2 rounded-full transition-all duration-200 hover:scale-105" style={{
                          background: 'rgba(156, 106, 222, 0.1)',
                          color: 'var(--color-lavender)'
                        }}>
                          <Share2 className="w-4 h-4" />
                          <span className="font-medium">Share</span>
                        </button>
                        <span className="flex items-center space-x-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                          <Clock className="w-3 h-3" />
                          <span>{post.timestamp.toLocaleDateString()}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>
              </Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityPanel;
